import { number, bool, string, func, arrayOf, object, shape } from 'prop-types';
import React, { PureComponent } from 'react';
import Card from '../../Card/Card';
import RangeChart from '../../RangeChart/RangeChart';
import InPlaceIndicator from '../../LoadingIndicator/InPlaceIndicator/InPlaceIndicator';
import SeeMore from '../../Modules/AddJob/SeeMore';
import { formatCurrencyIntl, translateSurveyStatus } from '../../lib/formatters';
import SurveyListSelect from '../../Surveys/SurveyListSelect';
import EditDataCutsModal from '../../Surveys/EditDataCutsModal';
import CommonErrorBoundary from '../../Util/CommonErrorBoundary';
import CardFooter from './CardFooter';

export default class CompanySourcedMatchCard extends PureComponent {
  static propTypes = {
    matchId: number,
    sourceMapId: number,
    payRange: arrayOf(number),
    payRangeScaleMin: number,
    payRangeScaleMax: number,
    jobTasks: string,
    className: string,
    loadingDetails: bool,
    jobCurrency: string,
    app: object,
    highVariance: bool,
    jobTitle: string,
    surveyCode: string,
    surveyStatus: bool,
    orgJobId: number,
    selectedDataCut: string,
    dataCuts: arrayOf(
      shape({
        job_scope: string
      })
    ),
    emptyDataCuts: arrayOf(
      shape({
        job_scope: string
      })
    ),
    onCutSelect: func,
    surveyDetails: object,
    loadDataCutsPanel: func,
    companyData: shape({
      total_companies: number,
      total_incumbents: number
    }),
    showIncludeExclude: bool,
    isPayscaleMatch: bool,
    typeOfMatch: string.isRequired,
    jobTaskMaxChar: number,
    companyTitle: string,
    readOnlyCard: bool,
    jobSurveyActions: func,
    surveyRow: object,
    isSuggestedSurvey: bool,
    idText: string, // This prop is for distinguishing range charts, which need a unique ID to render, ex :'-modal'
    isSurveyAlreadyMatched: bool,
    chartErrorMsg: string,
    jobCode: string,
    chartColors: arrayOf(string),
    getUpdatedDataCuts: func,
    loadDefaultDataCuts: func,
    getSurveySearchDefaultCuts: func,
    selectedProp: string,
    hasNoCutItems: bool,
    esSearchQuery: string
  };

  constructor(props) {
    super(props);

    this.bottomLabelFormatter = this.bottomLabelFormatter.bind(this);
    this._handleClickEditFavoriteDataCutsModal = this._handleClickEditFavoriteDataCutsModal.bind(this);
  }

  componentDidMount() {
    if (this.props.typeOfMatch === 'jobMatch') {
      this.props.loadDataCutsPanel(this.props.sourceMapId);
    }

    if (
      this.props.typeOfMatch === 'surveyMatch' &&
      this.props.getUpdatedDataCuts &&
      (!this.props.dataCuts || this.props.dataCuts.length <= 0)
    ) {
      if (this.props.surveyCode && this.props.jobCode) {
        this.props.getUpdatedDataCuts(this.props.surveyCode, this.props.jobCode);
      }
    }

    if (
      this.props.typeOfMatch === 'surveyMatch' &&
      this.props.loadDefaultDataCuts &&
      (!this.props.dataCuts || this.props.dataCuts.length <= 0)
    ) {
      if (this.props.sourceMapId) {
        this.props.loadDefaultDataCuts(this.props.sourceMapId);
      }
    }
  }

  bottomLabelFormatter(val, index) {
    const intl = {
      currency: this.props.jobCurrency,
      display: 'symbol',
      preferHourlyRate: false,
      useHourly: false,
      useThousands: true
    };

    return formatCurrencyIntl(val, intl, this.props.app);
  }

  _handleClickEditFavoriteDataCutsModal() {
    this.editFavoriteDataCutsModal.show();
    if (this.props.getSurveySearchDefaultCuts) {
      this.props.getSurveySearchDefaultCuts(this.props.sourceMapId);
    }
  }

  render() {
    const {
      payRange,
      payRangeScaleMax,
      payRangeScaleMin,
      jobTasks,
      matchId,
      sourceMapId,
      className,
      loadingDetails,
      highVariance,
      jobTitle,
      surveyCode,
      selectedDataCut,
      dataCuts,
      surveyDetails,
      onCutSelect,
      surveyStatus,
      companyData,
      orgJobId,
      loadDataCutsPanel,
      showIncludeExclude,
      isPayscaleMatch,
      typeOfMatch,
      jobTaskMaxChar,
      companyTitle,
      readOnlyCard,
      surveyRow,
      jobSurveyActions,
      isSuggestedSurvey,
      isSurveyAlreadyMatched,
      chartErrorMsg,
      chartColors,
      jobCode,
      selectedProp,
      hasNoCutItems,
      esSearchQuery
    } = this.props;

    let { emptyDataCuts } = this.props;
    if (emptyDataCuts) {
      emptyDataCuts = emptyDataCuts.map(emptyDataCut => {
        emptyDataCut.emptyItem = true;
        return emptyDataCut;
      });
    }

    let inclusionClass = '';
    let translatedSurveyStatus = translateSurveyStatus(surveyStatus);
    const companySurveyIncluded = translatedSurveyStatus === 'Included';
    if (!companySurveyIncluded) {
      inclusionClass = 'excluded';
    }

    let topRightCornerItem = <div className="current-match-card__change-match" />;

    if (typeOfMatch === 'surveyMatch' && !readOnlyCard && !isSuggestedSurvey) {
      const removeMatch = jobSurveyActions('removeJobMatch', 'get function');

      topRightCornerItem = (
        <div className="current-match-card__change-match">
          <button
            className="current-match-card__remove-match-button"
            onClick={removeMatch.bind(this, surveyRow, orgJobId)}
          >
            <i className="icon-cancel suggested-match__remove-cancel--icon" />
            Remove this Match
          </button>
        </div>
      );
    }

    if (
      typeOfMatch === 'surveyMatch' &&
      !readOnlyCard &&
      isSuggestedSurvey &&
      !isSurveyAlreadyMatched &&
      !hasNoCutItems
    ) {
      const makeMatch = jobSurveyActions('approveJobMatch', 'get function');
      topRightCornerItem = (
        <div className="current-match-card__change-match">
          <button className="current-match-card__make-match-button" onClick={makeMatch.bind(this, surveyRow, orgJobId)}>
            <span>Make this Match</span>
          </button>
        </div>
      );
    }

    return (
      <CommonErrorBoundary>
        <Card className={`current-match-card ${className ? className : ''}`} raiseOnHover={false}>
          <div className={`current-match-card__content ${inclusionClass}`}>
            <div className="current-match-card__title">
              <h4 className="current-match-card__company-title">
                {companyTitle ? companyTitle : 'PayScale Company Sourced'}
              </h4>
              <div className="current-match-card__data-cuts div--surveyMatchModalBody">
                <div className="current-match-card__data-cuts-label">Data Cut:</div>
                {!readOnlyCard ? (
                  <div>
                    <SurveyListSelect
                      showSelectedValue
                      sections={[dataCuts, emptyDataCuts]}
                      headers={['Favorite data cuts', 'Favorite data cuts with no data for this job']}
                      emptyTexts={['No favorite data cuts for this survey']}
                      links={[{ text: 'Add favorite data cuts', action: this._handleClickEditFavoriteDataCutsModal }]}
                      selectedProp={selectedProp ? selectedProp : 'job_scope'}
                      selectedValue={selectedDataCut}
                      displayProp="job_scope"
                      onSelect={onCutSelect}
                      history={history}
                      emptyDataProp={'emptyItem'}
                      truncateSelection={false}
                      disabled={!companySurveyIncluded}
                      matchId={matchId}
                      sourceMapId={sourceMapId}
                    />
                    <EditDataCutsModal
                      sourceMapId={sourceMapId}
                      surveyCode={surveyCode}
                      surveyDetails={surveyDetails}
                      loadDataCutsPanel={loadDataCutsPanel}
                      jobCode={jobCode}
                      ref={editFavoriteDataCutsModal => {
                        this.editFavoriteDataCutsModal = editFavoriteDataCutsModal;
                      }}
                      typeOfMatch={typeOfMatch}
                      esSearchQuery={esSearchQuery}
                      orgJobId={orgJobId}
                    />
                  </div>
                ) : (
                  <span className="current-match-card__selected-data-cuts">{selectedDataCut}</span>
                )}
              </div>
            </div>
            <div className="current-match-card__details">
              <div className="current-match-card__tasks-title">
                <span>{jobTitle}</span>
                {typeOfMatch === 'surveyMatch' && !readOnlyCard && !isSuggestedSurvey && !surveyRow.surveyEditMode ? (
                  <button
                    className="current-match-card__edit-survey--link"
                    onClick={this.props.jobSurveyActions('editSurveyMode', 'get function').bind(this, surveyRow)}
                  >
                    Edit Survey
                  </button>
                ) : null}
              </div>
              {typeOfMatch === 'surveyMatch' ? (
                <div className="current-match-card__job-code">Job Code: {jobCode ? jobCode : ''}</div>
              ) : null}
              <div className="current-match-card__tasks-text">
                <SeeMore maxChar={jobTaskMaxChar ? jobTaskMaxChar : 422}>{jobTasks}</SeeMore>
              </div>
              <div className="current-match-card__pay">
                {loadingDetails && (
                  <div className="current-match-card__loading">
                    <InPlaceIndicator />
                  </div>
                )}
                {!loadingDetails && payRange && payRange.length > 0 && (
                  <RangeChart
                    className="match-range"
                    labelsVisible={true}
                    data={payRange}
                    height={25}
                    width={420}
                    scaleMax={payRangeScaleMax}
                    scaleMin={payRangeScaleMin}
                    barHeight={20}
                    bottomLabelFormatter={this.bottomLabelFormatter}
                    hideTopLabels
                    labelToBarSpacingTop={5}
                    labelTextAnchor={['start', 'middle', 'end']}
                    invertLabels={false}
                    labelFontSize={11}
                    colors={chartColors ? chartColors : ['#22d6dd', '#a7eff1']}
                    id={`company-match${matchId}-market-range${this.props.idText}`}
                    hasLine={!highVariance}
                    highVariance={highVariance}
                  />
                )}
                {!loadingDetails && (!payRange || payRange.length === 0) && (
                  <div className="current-match-card__no-data">
                    <i className="icon-warning" />
                    {chartErrorMsg ? chartErrorMsg : 'No salary data available'}
                  </div>
                )}
              </div>
            </div>
            {topRightCornerItem}
          </div>
          <CardFooter
            typeOfMatch={typeOfMatch}
            surveyCode={surveyCode}
            isPayscaleMatch={isPayscaleMatch}
            surveyStatus={surveyStatus}
            showIncludeExclude={showIncludeExclude}
            matchId={matchId}
            companyData={companyData}
            surveyRow={surveyRow}
            jobSurveyActions={jobSurveyActions}
            orgJobId={orgJobId}
            readOnlyCard={readOnlyCard}
            isSuggestedSurvey={isSuggestedSurvey}
          />
        </Card>
      </CommonErrorBoundary>
    );
  }
}
